// Wars: declared as the political action against any rival (stronger ones too), for the card's military actions
//   (twice as many against Gandhi), but not in the last round, nor on a rival a pact says the declarer cannot attack (a
//   pact between them that ends on an attack ends). Nothing happens then except for the card's lasting effect (Hybrid
//   War: -2 happy faces for both sides while it is in play). It is resolved at the start of the declarer's next turn:
//   strengths are compared then (the declarer's with what pacts add for attacking this rival, e.g. Open Borders), so both
//   sides have a turn to build up, and nothing can be played to change it. The stronger side is
//   the victor (the declarer can lose); the difference is the advantage; a tie does nothing. The victor takes what the
//   card says, as much of it as the defeated civilization has. If the game ends first, the war does nothing.
//   A player can be at war with several rivals at once (declaring one and having others declared on them).
// Wars waiting to be resolved are kept in the game state: [{ card, aggressor, defender, round }], and while the victor
// chooses (War Over Technology), the one being resolved: { ..., victor, loser, advantage, statsBefore }.
const CardConverter = require('../Utilities/Converters/CardConverter');
const CardToData = require('../Utilities/Converters/CardToData');
const WarNames = require('../Constants/Names/WarNames');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { Rules, HasRuleException, Add } = require('../Effects/Effects');
const { RequestDecision } = require('./Decisions');
const { GetAllStats, PlayerChanges } = require('../Utilities/Stats');
const { CardRef } = require('../Utilities/CardRefs');

const SpecialTypes = Object.values(TechnologyTypes.Special);
const Level = (card) => Math.max(0, require('../Constants/Constants').AgeOrder.indexOf(card.age));
const takeUpTo = (available, amount) => Math.max(0, Math.min(available, amount));

// ---------------- Declaring

// Military actions a war costs against this rival: more against one protected so (Gandhi, see Aggression ProtectedCost)
const WarCost = (card, defender) => require('./Aggression').ProtectedCost(card.war.cost, defender);

// Why the war cannot be declared on this rival now, or null
const WarProblem = (aggressor, defender, card) => {
    if (HasRuleException(aggressor, Rules.NoAggression))
    {
        return "Your leader does not allow wars";
    }
    if (!defender || defender === aggressor || defender.resigned)
    {
        return "Choose a rival";
    }
    if (aggressor.gameState.finalRound === aggressor.gameState.round)
    {
        return "No war can be declared in the last round";
    }
    // Loss of Sovereignty; a pact that says the two cannot attack each other
    const pact = require('./Pact').WarDeclarationProblem(aggressor.gameState, defender)
        ?? require('./Pact').AttackProblem(aggressor.gameState, aggressor, defender);
    if (pact)
    {
        return pact;
    }
    const cost = WarCost(card, defender);
    return aggressor.GetMilitaryActionsRemaining() < cost ? `Needs ${cost} military action${cost === 1 ? '' : 's'}` : null;
};

// For the target picker: each rival's strength and what the war would cost against them
const WarTargets = (gameState, aggressor, card) => gameState.playerStates.filter(p => p !== aggressor && !p.resigned).map(defender => ({
    player: defender.playerNumber,
    name: defender.name,
    strength: defender.GetMilitaryStrength(),
    cost: WarCost(card, defender),
    baseCost: card.war.cost,
    problem: WarProblem(aggressor, defender, card),
    // Himeji Castle: only whether they have it, no number, as a bigger unit may be built before the war resolves
    himejiDefense: require('./Aggression').HasHimeji(defender) ? 1 : 0,
    // Pacts it would end, and strength added for attacking this rival (as for an aggression)
    breaks: require('./Pact').EndedByAttack(gameState, aggressor, defender).map(pact => `Ends your ${pact.card.name} with ${defender.name}`),
    attackBonus: require('./Pact').AttackBonus(gameState, aggressor, defender),
    shortfall: null,
}));

// ended: lines for the pacts declaring it ended, listed under it
const DeclareWar = (gameState, aggressor, defender, card, ended = []) => {
    gameState.wars.push({ card: CardToData(card), aggressor: aggressor.playerNumber, defender: defender.playerNumber, round: gameState.round });
    gameState.reports.push({
        name: card.name, age: card.age, card: CardToData(card), revealedBy: aggressor.name, round: gameState.round, target: defender.name,
        // (when it resolves, and how it would now, the recap works out from the war in play)
        kind: 'war', text: '', lines: ended, changes: [],
    });
};

// ---------------- While in play

// What a war in play does to both sides until it is resolved (built into the players when the game state is read)
const Lasting = {
    [WarNames.HybridWar]: (player, card) => Add(player, card, 'happyFaces', -2),
};
const ApplyLastingEffects = (gameState) => gameState.wars.forEach(war => {
    const lasting = Lasting[war.card.name];
    if (lasting)
    {
        const card = CardConverter(war.card);
        lasting(gameState.playerStates[war.aggressor], card);
        lasting(gameState.playerStates[war.defender], card);
    }
});

// ---------------- What the victor takes
// options(victor, loser, advantage): the victor's choice (a checked decision's context), or null when there is none;
// apply(gameState, victor, loser, advantage, value): what it does, as log text; preview: the same without a choice.

// Special technologies the victor could take: worth no more than the advantage, and not one they already have in play
// or in hand
const TakeableTechnologies = (victor, loser, advantage) => loser.technologies
    .filter(tech => SpecialTypes.includes(tech.type) && tech.scienceCost <= advantage && !victor.HasTechnology(tech.name)
        && !victor.civilHand.some(card => card.name === tech.name))
    .map(tech => ({ name: tech.name, type: tech.type, cost: tech.scienceCost, age: tech.age }));

const TechnologyProblem = (victor, loser, advantage, names) => {
    const takeable = TakeableTechnologies(victor, loser, advantage);
    if (!Array.isArray(names) || new Set(names).size !== names.length || names.some(name => !takeable.some(t => t.name === name)))
    {
        return "Choose special technologies that can be taken";
    }
    const cost = names.reduce((sum, name) => sum + takeable.find(t => t.name === name).cost, 0);
    return cost > advantage ? `Those are worth ${cost}, more than the advantage of ${advantage}` : null;
};

// statsOnly: what it does is just numbers changing hands, which the recap shows (its text is left out there)
const Effects = {
    [WarNames.WarOverCulture]: {
        statsOnly: true,
        apply: (gameState, victor, loser, advantage) => {
            const culture = takeUpTo(loser.culture, 5 + advantage);
            loser.culture -= culture;
            victor.culture += culture;
            return `${victor.name} takes ${culture} culture from ${loser.name}`;
        },
    },
    [WarNames.HybridWar]: {
        statsOnly: true,
        apply: (gameState, victor, loser, advantage) => {
            const culture = takeUpTo(loser.culture, advantage);
            loser.culture -= culture;
            victor.culture += culture;
            return `${victor.name} takes ${culture} culture from ${loser.name}`;
        },
    },
    [WarNames.WarOverTerritory]: {
        statsOnly: true,
        // From the yellow bank only: never workers, used or not
        apply: (gameState, victor, loser, advantage) => {
            const tokens = takeUpTo(loser.yellowBank, 1 + Math.floor(advantage / 5));
            if (tokens > 0)
            {
                loser.ChangeTokens('yellow', -tokens, WarNames.WarOverTerritory);
                victor.ChangeTokens('yellow', tokens, WarNames.WarOverTerritory);
            }
            return `${victor.name} takes ${tokens} yellow token${tokens === 1 ? '' : 's'} from ${loser.name}`;
        },
    },
    [WarNames.WarOverTechnology]: {
        options: (victor, loser, advantage) => {
            const technologies = TakeableTechnologies(victor, loser, advantage);
            return technologies.length > 0 ? { advantage, technologies, science: loser.science } : null;
        },
        // value: the special technologies taken (their cost counts towards the advantage); the rest as science
        apply: (gameState, victor, loser, advantage, names = []) => {
            const taken = loser.technologies.filter(tech => names.includes(tech.name));
            const discarded = [];
            taken.forEach(tech => {
                loser.technologies = loser.technologies.filter(t => t !== tech);
                // One special technology of each type: the higher level stays in play, the other is discarded
                const own = victor.technologies.find(t => t.type === tech.type);
                if (own && Level(own) >= Level(tech))
                {
                    discarded.push(tech);
                    return;
                }
                victor.technologies = victor.technologies.filter(t => t.type !== tech.type);
                victor.AddTechnology(tech);
            });
            const science = takeUpTo(loser.science, advantage - taken.reduce((sum, tech) => sum + tech.scienceCost, 0));
            loser.science -= science;
            victor.science += science;
            // (the technologies as their cards, so the recap shows what changed hands)
            const parts = [...taken.map(TechRef), `${science} science`];
            const kept = discarded.length ? ` (${discarded.map(TechRef).join(', ')} discarded: ${victor.name} keeps a higher level of that type)` : '';
            return `${victor.name} takes ${parts.join(' and ')} from ${loser.name}${kept}`;
        },
        preview: (victor, loser, advantage) => {
            const technologies = TakeableTechnologies(victor, loser, advantage);
            return `${takeUpTo(loser.science, advantage)} science${technologies.length ? ' (and/or special technologies)' : ''}`;
        },
    },
};

// A technology named in text, drawn as its card
const TechRef = (tech) => require('../Utilities/CardRefs').CardRef({ type: 'Technology', name: tech.name, age: tech.age });

// ---------------- Resolving

// The declarer's strength in the war: with what pacts add to attacking this rival (Open Borders Agreement)
const AttackingStrength = (gameState, war) => {
    const aggressor = gameState.playerStates[war.aggressor];
    return aggressor.GetMilitaryStrength() + require('./Pact').AttackBonus(gameState, aggressor, gameState.playerStates[war.defender]);
};

// Who would win now, by how much, and what they would take (for the war's notice; takes nothing)
// added: strength added to either side, by player number (e.g. a unit Himeji Castle adds)
const WarOutcome = (gameState, war, added = {}) => {
    const aggressor = gameState.playerStates[war.aggressor];
    const defender = gameState.playerStates[war.defender];
    const a = AttackingStrength(gameState, war) + (added[war.aggressor] ?? 0);
    const d = defender.GetMilitaryStrength() + (added[war.defender] ?? 0);
    if (a === d)
    {
        return { aggressorStrength: a, defenderStrength: d, victor: null, advantage: 0, text: "a tie: nothing would happen" };
    }
    const [victor, loser] = a > d ? [aggressor, defender] : [defender, aggressor];
    const advantage = Math.abs(a - d);
    const effect = Effects[war.card.name];
    // What apply would do, worked out on copies
    let takes;
    // (War Over Technology: the advantage as science; which special technologies could be taken instead the card says)
    if (war.card.name === WarNames.WarOverTechnology)
    {
        takes = `${advantage} science`;
    } else if (effect.preview)
    {
        takes = effect.preview(victor, loser, advantage);
    } else {
        const copy = new (require('../entities/GameState'))(JSON.parse(JSON.stringify(gameState.toData())));
        takes = effect.apply(copy, copy.playerStates[victor.playerNumber], copy.playerStates[loser.playerNumber], advantage)
            .replace(`${victor.name} takes `, '').replace(` from ${loser.name}`, '');
    }
    return {
        aggressorStrength: a, defenderStrength: d, victor: victor.playerNumber, loser: loser.playerNumber, advantage, takes,
        text: `${victor.name} would win by ${advantage} and take ${takes} from ${loser.name}`,
    };
};

// Everyone's stats as the state now stands, worked out on a rebuilt copy: cards that changed hands (a special
// technology taken, replacing the victor's own) only count, or stop counting, once the state is read again
const FreshStats = (gameState) => GetAllStats(new (require('../entities/GameState'))(JSON.parse(JSON.stringify(gameState.toData()))));

// lines: how it went, a line each, under the war in the recap ("Dave wins (6 strength vs 4 strength)")
const finish = (gameState, war, lines) => {
    gameState.militaryDiscard.push(CardConverter(war.card));
    gameState.reports.push({
        name: war.card.name, age: war.card.age, card: war.card, revealedBy: gameState.playerStates[war.aggressor].name, round: gameState.round,
        target: gameState.playerStates[war.defender].name, text: '', lines, kind: 'war', resolved: true,
        changes: PlayerChanges(war.statsBefore, FreshStats(gameState)),
    });
    gameState.wars = gameState.wars.filter(w => w !== war);
};

// At the start of a player's turn: the war they declared (if any) is resolved
// (with Himeji Castle on either side: first each such side is asked whether to add a unit, the turn waiting; the war is
// resolved once they have answered, see Decisions himejiWar)
const ResolveWars = (gameState, player) => {
    gameState.wars.filter(war => war.aggressor === player.playerNumber).forEach(war => {
        if (!war.himeji && AskHimeji(gameState, war))
        {
            return;
        }
        ResolveWar(gameState, war);
    });
};

// Asks each side of the war with Himeji Castle (and a unit) whether to add one; false when there is no one to ask
const AskHimeji = (gameState, war) => {
    const { HimejiUnits } = require('./Aggression');
    const sides = [war.aggressor, war.defender].map(number => gameState.playerStates[number]).filter(side => HimejiUnits(side).length > 0);
    if (sides.length === 0)
    {
        return false;
    }
    war.himeji = { asked: sides.map(side => side.playerNumber), units: {} };
    const aggressor = gameState.playerStates[war.aggressor];
    const defender = gameState.playerStates[war.defender];
    sides.forEach(side => RequestDecision(gameState, {
        player: side, type: 'himejiWar', event: war.card.name, blocking: true, ui: 'himejiWar',
        prompt: `Himeji Castle: add one of your units' strength to yours in ${CardRef(war.card)}? It is sacrificed after.`,
        // (each with what the war would come to with it: who wins, and what is taken)
        options: [...HimejiUnits(side).map(unit => ({ label: `${unit.name} (+${unit.strength})`, value: unit.name, strength: unit.strength })),
            { label: 'No unit', value: null, strength: 0 }]
            .map(option => ({ ...option, outcome: WarOutcome(gameState, war, { [side.playerNumber]: option.strength }) })),
        // (for the decision's panel: the war as its card, both sides and their strengths as they stand, and whether the
        // other side may add a unit too)
        context: { warCard: war.card.id, card: war.card, aggressor: war.aggressor, defender: war.defender,
            strengths: { [war.aggressor]: AttackingStrength(gameState, war), [war.defender]: defender.GetMilitaryStrength() },
            otherMayAdd: sides.length > 1 },
    }));
    return true;
};

// The war compared and resolved (with any units Himeji Castle added, sacrificed after)
const ResolveWar = (gameState, war) => {
    const { HimejiStrength, SacrificeHimeji } = require('./Aggression');
    {
        const aggressor = gameState.playerStates[war.aggressor];
        const defender = gameState.playerStates[war.defender];
        war.statsBefore = GetAllStats(gameState);
        const units = war.himeji?.units ?? {};
        // (in a war there is no attacker or defender: just the unit's strength, on either side)
        const added = (side) => units[side.playerNumber] ? HimejiStrength(side, units[side.playerNumber]) : 0;
        const a = AttackingStrength(gameState, war) + added(aggressor);
        const d = defender.GetMilitaryStrength() + added(defender);
        const sacrificed = SacrificeHimeji(gameState, units);
        const strengths = `${a} strength vs ${d} strength${sacrificed.length ? `, with Himeji Castle: ${sacrificed.join(', ')}` : ''}`;
        if (a === d)
        {
            finish(gameState, war, [`A tie (${strengths}): nothing happens`]);
            return;
        }
        const [victor, loser] = a > d ? [aggressor, defender] : [defender, aggressor];
        Object.assign(war, { victor: victor.playerNumber, loser: loser.playerNumber, advantage: Math.abs(a - d), strengths });
        const effect = Effects[war.card.name];
        const context = effect.options?.(victor, loser, war.advantage);
        if (context)
        {
            war.resolving = true;
            RequestDecision(gameState, {
                player: victor, type: 'warChoice', event: war.card.name, blocking: true, ui: 'warTechnology', options: null,
                prompt: `${CardRef(war.card)}: you win by ${war.advantage}. Take special technologies (worth up to the advantage) and the rest as science.`,
                context: { ...context, war: war.card.name },
            });
            return;
        }
        const text = effect.apply(gameState, victor, loser, war.advantage);
        finish(gameState, war, [`${victor.name} wins (${strengths})`, ...(effect.statsOnly ? [] : [text])]);
    }
};

// The victor's choice (War Over Technology): which special technologies they take
const WarChoiceProblem = (gameState, decision, value) => {
    const war = gameState.wars.find(w => w.resolving);
    return TechnologyProblem(gameState.playerStates[war.victor], gameState.playerStates[war.loser], war.advantage, value);
};
const WarChoice = (gameState, decision, value) => {
    const war = gameState.wars.find(w => w.resolving);
    const victor = gameState.playerStates[war.victor];
    const text = Effects[war.card.name].apply(gameState, victor, gameState.playerStates[war.loser], war.advantage, value);
    finish(gameState, war, [`${victor.name} wins (${war.strengths})`, text]);
    return text;
};

module.exports = {
    WarCost,
    WarProblem,
    WarTargets,
    DeclareWar,
    ApplyLastingEffects,
    WarOutcome,
    ResolveWars,
    ResolveWar,
    WarChoiceProblem,
    WarChoice,
    TakeableTechnologies,
    WarEffects: Effects,
};
